import type {
  Instructor,
  BookedServiceDetailModel,
  ServiceCreate,
  BookedServiceSessionModel,
  Service,
  NewBookingSettings,
  AvailabilityData,
  FacilityAssetDisplayItem,
  FacilityAssetFormData,
  ServiceUpdate,
  PaginatedServiceList,
  PaginatedInstructorList,
} from "./types";
import {
  adminV1OrganizationsPaymentsServicesList,
  adminV1OrganizationsPaymentsServicesCreate,
  adminV1OrganizationsPaymentsServiceInstructorsList,
  adminV1OrganizationsPaymentsServiceInstructorsCreate,
  adminV1OrganizationsPaymentsServiceInstructorsRetrieve,
  adminV1OrganizationsPaymentsServiceInstructorsUpdate,
  adminV1OrganizationsPaymentsServiceInstructorsDestroy,
  adminV1OrganizationsPaymentsServiceBookingsCreate,
  adminV1OrganizationsPaymentsServiceBookingsDestroy,
  adminV1OrganizationsPaymentsServiceBookingsRefundCreate,
  adminV1OrganizationsBookingSettingsCreate,
  adminV1OrganizationsBookingSettingsUpdate,
  adminV1OrganizationsBookingSettingsRetrieve,
  adminV1OrganizationsPaymentsServicesRetrieve,
  adminV1OrganizationsPaymentsServicesUpdate,
  adminV1OrganizationsPaymentsServicesDestroy,
  adminV1OrganizationsPaymentsServicesReorderCreate,
} from "@/api/admin-payments-services/admin-payments-services";
import {
  translateServiceList,
  translateServiceCreateToApiAdminService,
  translateInstructorList,
  translateAdminBookingSessionDetailToBookedServiceDetailModel,
  translateAdminBookingSessionToBookedServiceSessionModel,
  translateNewBookingSettingsToApiAdminBookingSettings,
  translateApiBookingSettingsToBookingSettingsModel,
  translateService,
  translateServiceEditToApiAdminService,
  translateApiAvailabilityToAvailabilityData,
  translateAdminServiceInstructorToInstructorFormData,
  translateInstructorFormDataToApiPayload,
  translateServiceUpdateToApiAdminService,
} from "./translator";
import {
  adminV1OrganizationsBookingSessionsList,
  adminV1OrganizationsBookingSessionsRetrieve,
  adminV1OrganizationsBookingSessionsDestroy,
  adminV1OrganizationsBookingSessionsRescheduleCreate,
} from "@/api/admin-payments-booking-sessions/admin-payments-booking-sessions";

import type {
  AdminV1OrganizationsBookingSessionsListParams,
  AdminBookingSessionDetail,
  AdminServiceBooking,
  AdminBookingSettings,
  BookingSessionRescheduleInput,
  AdminV1OrganizationsPaymentsServicesListParams,
  AdminV1OrganizationsPaymentsServiceInstructorsListParams,
  AdminV1OrganizationsBookingSessionAssignmentsFacilityRetrieveParams,
  AdminBookingRequest,
  AdminV1OrganizationsPaymentsServiceRatesInstructorsAvailabilityListParams,
  LightweightAdminCoach,
} from "@/api/model";
import { format } from "date-fns";
import { adminV1OrganizationsCoachesList } from "@/api/admin-coaches/admin-coaches";
import { useCurrentOrganization } from "@/composables/useCurrentOrganization";
import { OrganizationTypeEnum } from "@/api/model/organizationTypeEnum";

function getDefaultOrganizationId(): number {
  const { currentOrganizationId } = useCurrentOrganization();
  const orgId = currentOrganizationId.value;
  if (!orgId) {
    throw new Error("No default organization found");
  }
  return orgId;
}
import { adminV1OrganizationsPaymentsServiceRatesInstructorsAvailabilityList } from "@/api/admin-payments-service-availability/admin-payments-service-availability";
import type { AdminServiceInstructor as ApiAdminServiceInstructor } from "@/api/model";
import type { InstructorFormData } from "./types";
import {
  adminV1OrganizationsPaymentsFacilityAssetsList,
  adminV1OrganizationsPaymentsFacilityAssetsCreate,
  adminV1OrganizationsPaymentsFacilityAssetsRetrieve,
  adminV1OrganizationsPaymentsFacilityAssetsUpdate,
  adminFacilityAssetDestroy,
} from "@/api/admin-payments-facility-assets/admin-payments-facility-assets";
import {
  translatePaginatedFacilityAssetListToDisplayItems,
  translateFacilityAssetFormDataToApiPayload,
  translateAdminFacilityAssetToDisplayItem,
  translateAdminFacilityAssetToFormData,
  translateFacilityAssetFormDataToApiUpdatePayload,
} from "./facility-asset-translator";
import type { AdminFacilityAsset } from "@/api/model";
import { adminV1OrganizationsBookingSessionAssignmentsFacilityRetrieve } from "@/api/admin-payments-booking-session-assignments/admin-payments-booking-session-assignments";

export const getServices = async (
  params?: AdminV1OrganizationsPaymentsServicesListParams
): Promise<PaginatedServiceList> => {
  try {
    // The services endpoint 412s ("Organization N not configured for payments")
    // for any org without payment settings. Two such cases reach this caller:
    //  - Network orgs structurally never have payment settings. Network admins
    //    impersonate clubs, but the current org falls back to the network
    //    whenever they're not actively impersonating (pre-launch, after exit, or
    //    a navigation/reload that drops the ?impersonateOrg= param).
    //  - Regular clubs that simply haven't enabled payments yet.
    // Both are knowable up front from the current org, so skip the request
    // entirely rather than firing a call that always fails. (payments_enabled
    // tracks the backend's payment_settings state — a club has no settings row
    // until it's enabled — so it catches the clubs the org_type check misses.)
    const { currentOrganization } = useCurrentOrganization();
    if (
      currentOrganization.value?.organization_type ===
        OrganizationTypeEnum.network ||
      currentOrganization.value?.payments_enabled === false
    ) {
      return { count: 0, next: null, previous: null, results: [] };
    }

    const organizationId = getDefaultOrganizationId();
    const response = await adminV1OrganizationsPaymentsServicesList(
      organizationId,
      params
    );
    return {
      ...response,
      results: translateServiceList(response.results ?? []),
    };
  } catch (error) {
    // The services endpoint lives behind the backend's payment-settings guard
    // (tfa/lib/payments/payabli/validation.py). When an org has no payment
    // settings configured / enabled it returns 412 ("Organization N not
    // configured for payments"). That's an expected state for non-payments
    // orgs — many callers (benefit drawers, booking views) hit this on mount,
    // so treat it as "no services" instead of erroring and spamming Sentry.
    const status =
      (error as { response?: { status?: number } })?.response?.status ??
      (error as { status?: number })?.status;
    if (status === 412) {
      return { count: 0, next: null, previous: null, results: [] };
    }

    console.error("Error fetching services:", error);
    throw error;
  }
};

export const getService = async (serviceId: string): Promise<Service> => {
  const organizationId = getDefaultOrganizationId();
  const response = await adminV1OrganizationsPaymentsServicesRetrieve(
    organizationId,
    serviceId
  );
  return translateService(response);
};

export const getBookings = async (
  startDate: Date,
  endDate: Date
): Promise<BookedServiceSessionModel[]> => {
  try {
    const params: AdminV1OrganizationsBookingSessionsListParams = {
      start_date: format(startDate, "yyyy-MM-dd"),
      end_date: format(endDate, "yyyy-MM-dd"),
    };
    const organizationId = getDefaultOrganizationId();
    const apiResponse = await adminV1OrganizationsBookingSessionsList(
      organizationId,
      params
    );
    const sessions = apiResponse ?? [];
    const bookedSessions = sessions.map(
      translateAdminBookingSessionToBookedServiceSessionModel
    );
    return bookedSessions;
  } catch (error) {
    console.error("Error fetching booking sessions:", error);
    throw error;
  }
};

export const getInstructors = async (
  params?: AdminV1OrganizationsPaymentsServiceInstructorsListParams
): Promise<PaginatedInstructorList> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const response = await adminV1OrganizationsPaymentsServiceInstructorsList(
      organizationId,
      params
    );
    return translateInstructorList(response);
  } catch (error) {
    console.error("Error fetching instructors:", error);
    throw error;
  }
};

export const getInstructor = async (
  instructorId: string
): Promise<InstructorFormData> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const apiInstructor: ApiAdminServiceInstructor =
      await adminV1OrganizationsPaymentsServiceInstructorsRetrieve(
        organizationId,
        instructorId
      );
    return translateAdminServiceInstructorToInstructorFormData(apiInstructor);
  } catch (error: any) {
    console.error(
      `Error fetching instructor with ID ${instructorId}:`,
      error.response?.data || error.message || error
    );
    throw error;
  }
};

export const getCoaches = async (
  orgId: number,
  limit: number,
  offset: number
): Promise<LightweightAdminCoach[]> => {
  const organizationId = getDefaultOrganizationId();
  const response = await adminV1OrganizationsCoachesList(organizationId, {
    limit: limit,
    offset: offset,
  });
  return response.results ?? [];
};

export const postInstructor = async (
  data: InstructorFormData
): Promise<InstructorFormData> => {
  try {
    const apiPayload: any = translateInstructorFormDataToApiPayload(data);
    const organizationId = getDefaultOrganizationId();
    const createdApiInstructor: ApiAdminServiceInstructor =
      await adminV1OrganizationsPaymentsServiceInstructorsCreate(
        organizationId,
        apiPayload
      );
    return translateAdminServiceInstructorToInstructorFormData(
      createdApiInstructor
    );
  } catch (error: any) {
    console.error(
      "Error creating instructor:",
      error.response?.data || error.message || error
    );
    throw error;
  }
};

export const createService = async (
  newServiceData: ServiceCreate
): Promise<Service> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsPaymentsServicesCreate(
      organizationId,
      translateServiceCreateToApiAdminService(newServiceData)
    );
    return translateService(result);
  } catch (error) {
    console.error("Error creating service:", error);
    throw error;
  }
};

export const updateService = async (
  serviceId: string,
  newServiceData: ServiceUpdate,
  baseService: Service
): Promise<Service> => {
  try {
    const apiServicePayload = translateServiceUpdateToApiAdminService(
      newServiceData,
      baseService
    );

    const organizationId = getDefaultOrganizationId();
    const adminServiceResponse =
      await adminV1OrganizationsPaymentsServicesUpdate(
        organizationId,
        serviceId,
        apiServicePayload
      );

    return translateService(adminServiceResponse);
  } catch (error) {
    console.error("Error updating service:", error);
    throw error;
  }
};

export const deleteService = async (serviceId: string): Promise<void> => {
  try {
    const organizationId = getDefaultOrganizationId();
    await adminV1OrganizationsPaymentsServicesDestroy(
      organizationId,
      serviceId
    );
  } catch (error: any) {
    console.error(
      `Error deleting service with ID ${serviceId}:`,
      error.response?.data || error.message || error
    );
    throw error;
  }
};

export const reorderServicesApi = async (
  serviceIds: string[]
): Promise<void> => {
  try {
    const organizationId = getDefaultOrganizationId();
    await adminV1OrganizationsPaymentsServicesReorderCreate(organizationId, {
      service_ids: serviceIds,
    });
  } catch (error: any) {
    console.error(
      "Error reordering services:",
      error.response?.data || error.message || error
    );
    throw error;
  }
};

export const getBookedServiceDetail = async (
  bookedServiceId: string
): Promise<BookedServiceDetailModel | null> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const { currentOrganizationTimezone } = useCurrentOrganization();
    const apiDetail: AdminBookingSessionDetail =
      await adminV1OrganizationsBookingSessionsRetrieve(
        organizationId,
        bookedServiceId,
        {}
      );

    return translateAdminBookingSessionDetailToBookedServiceDetailModel(
      apiDetail,
      currentOrganizationTimezone.value
    );
  } catch (error: any) {
    if (error?.response?.status === 404) {
      console.warn(`No details found for bookedServiceId: ${bookedServiceId}`);
      return null;
    }
    console.error(
      `Error fetching booked service detail for ID ${bookedServiceId}:`,
      error.response?.data || error.message || error
    );
    throw error;
  }
};

export const cancelParticipantBooking = async (
  participantBookingId: string
): Promise<void> => {
  try {
    const organizationId = getDefaultOrganizationId();
    await adminV1OrganizationsPaymentsServiceBookingsDestroy(
      organizationId,
      participantBookingId
    );
  } catch (error: any) {
    console.error(
      `Error canceling participant booking ${participantBookingId}:`,
      error.response?.data || error.message || error
    );
    throw error;
  }
};

export const refundParticipantBooking = async (
  participantBookingId: string
): Promise<AdminServiceBooking> => {
  try {
    const refundPayload = {} as unknown as AdminServiceBooking;
    const organizationId = getDefaultOrganizationId();
    const result =
      await adminV1OrganizationsPaymentsServiceBookingsRefundCreate(
        organizationId,
        participantBookingId,
        refundPayload
      );

    return result;
  } catch (error: any) {
    console.error(
      `Error refunding participant booking ${participantBookingId}:`,
      error.response?.data || error.message || error
    );
    throw error;
  }
};

/**
 * Cancels an entire booked service session (all participants).
 * @param bookedServiceSessionId - The composite ID of the session instance.
 */
export const cancelBookedServiceSession = async (
  bookedServiceSessionId: string
): Promise<void> => {
  try {
    const organizationId = getDefaultOrganizationId();
    await adminV1OrganizationsBookingSessionsDestroy(
      organizationId,
      bookedServiceSessionId
    );
  } catch (error: any) {
    console.error(
      `Error canceling booked service session ${bookedServiceSessionId}:`,
      error.response?.data || error.message || error
    );
    throw error;
  }
};

export const createBookingSettings = async (
  orgPk: number,
  bookingSettings: NewBookingSettings
): Promise<AdminBookingSettings> => {
  const result = await adminV1OrganizationsBookingSettingsCreate(
    orgPk,
    translateNewBookingSettingsToApiAdminBookingSettings(bookingSettings)
  );
  return result;
};

export const updateBookingSettings = async (
  orgPk: number,
  bookingSettings: NewBookingSettings
): Promise<AdminBookingSettings> => {
  const result = await adminV1OrganizationsBookingSettingsUpdate(
    orgPk,
    translateNewBookingSettingsToApiAdminBookingSettings(bookingSettings)
  );
  return result;
};

export const getBookingSettings = async (
  orgPk: number
): Promise<NewBookingSettings | null> => {
  const result = await adminV1OrganizationsBookingSettingsRetrieve(orgPk);
  if (!result) {
    return null;
  }
  return translateApiBookingSettingsToBookingSettingsModel(result);
};

export const getServiceRateInstructorAvailability = async (
  serviceRateId: string,
  instructorId: string,
  startDate: string,
  endDate: string,
  reschedulingBookingId?: string
): Promise<AvailabilityData[] | null> => {
  try {
    const organizationId = getDefaultOrganizationId();

    const params: AdminV1OrganizationsPaymentsServiceRatesInstructorsAvailabilityListParams =
      {
        start_date: startDate,
        end_date: endDate,
      };

    if (reschedulingBookingId) {
      params.rescheduling_booking_id = reschedulingBookingId;
    }

    const result =
      await adminV1OrganizationsPaymentsServiceRatesInstructorsAvailabilityList(
        organizationId,
        serviceRateId,
        instructorId,
        params
      );

    if (!result) {
      return null;
    }
    return translateApiAvailabilityToAvailabilityData(result);
  } catch (error: any) {
    console.error(
      `Error fetching availability for service rate ${serviceRateId} and instructor ${instructorId}:`,
      error.response?.data || error.message || error
    );
    throw error;
  }
};

export const rescheduleBookingSession = async (
  bookedServiceId: string,
  newAvailabilitySlotId: string,
  overrideAvailability = false
): Promise<AdminBookingSessionDetail> => {
  try {
    const payload: BookingSessionRescheduleInput = {
      new_availability_slot_id: newAvailabilitySlotId,
      override_availability: overrideAvailability,
    };

    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsBookingSessionsRescheduleCreate(
      organizationId,
      bookedServiceId,
      payload
    );

    return result;
  } catch (error: any) {
    console.error(
      `Error rescheduling booking session ${bookedServiceId}:`,
      error.response?.data || error.message || error
    );
    throw error;
  }
};

export const updateInstructor = async (
  instructorId: string,
  data: InstructorFormData
): Promise<InstructorFormData> => {
  try {
    const apiPayload: any = translateInstructorFormDataToApiPayload(data);
    const organizationId = getDefaultOrganizationId();
    const updatedApiInstructor: ApiAdminServiceInstructor =
      await adminV1OrganizationsPaymentsServiceInstructorsUpdate(
        organizationId,
        instructorId,
        apiPayload
      );
    return translateAdminServiceInstructorToInstructorFormData(
      updatedApiInstructor
    );
  } catch (error: any) {
    console.error(
      `Error updating instructor with ID ${instructorId}:`,
      error.response?.data || error.message || error
    );
    throw error;
  }
};

export const getFacilityAssets = async (): Promise<
  FacilityAssetDisplayItem[]
> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const response =
      await adminV1OrganizationsPaymentsFacilityAssetsList(organizationId);
    return translatePaginatedFacilityAssetListToDisplayItems(response);
  } catch (error) {
    console.error("Error fetching facility assets:", error);
    throw error;
  }
};

export const createFacilityAssetInApi = async (
  formData: FacilityAssetFormData
): Promise<FacilityAssetDisplayItem> => {
  try {
    const apiPayload = translateFacilityAssetFormDataToApiPayload(formData);
    const organizationId = getDefaultOrganizationId();
    const createdApiAsset: AdminFacilityAsset =
      await adminV1OrganizationsPaymentsFacilityAssetsCreate(
        organizationId,
        apiPayload as any
      );
    return translateAdminFacilityAssetToDisplayItem(createdApiAsset);
  } catch (error) {
    throw error;
  }
};

export const getFacilityAssetByIdFromApi = async (
  assetId: string
): Promise<FacilityAssetFormData | null> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const response = await adminV1OrganizationsPaymentsFacilityAssetsRetrieve(
      organizationId,
      assetId
    );
    const apiAsset: AdminFacilityAsset = response;

    if (apiAsset) {
      return translateAdminFacilityAssetToFormData(apiAsset);
    }
    console.warn(
      `Facility asset with ID ${assetId} not found or API returned no data.`
    );
    return null;
  } catch (error: any) {
    if (error?.response?.status === 404 || error?.status === 404) {
      console.warn(`Facility asset with ID ${assetId} not found (404).`);
      return null;
    }
    console.error(
      `Error fetching facility asset by ID ${assetId}:`,
      error.response?.data || error.message || error
    );
    throw error;
  }
};

export const updateExistingFacilityAssetInApi = async (
  assetId: string,
  formData: FacilityAssetFormData
): Promise<FacilityAssetFormData> => {
  try {
    const apiPayload =
      translateFacilityAssetFormDataToApiUpdatePayload(formData);
    const organizationId = getDefaultOrganizationId();
    const updatedApiAsset: AdminFacilityAsset =
      await adminV1OrganizationsPaymentsFacilityAssetsUpdate(
        organizationId,
        assetId,
        apiPayload as any
      );

    return translateAdminFacilityAssetToFormData(updatedApiAsset);
  } catch (error: any) {
    console.error(
      `Error updating facility asset with ID ${assetId}:`,
      error.response?.data || error.message || error
    );
    throw error;
  }
};

export const deleteFacilityAssetByIdFromApi = async (
  assetId: string
): Promise<void> => {
  try {
    const organizationId = getDefaultOrganizationId();
    await adminFacilityAssetDestroy(organizationId, assetId);
  } catch (error: any) {
    console.error(
      `Error deleting facility asset with ID ${assetId}:`,
      error.response?.data || error.message || error
    );
    throw error;
  }
};

export const deleteInstructorByIdFromApi = async (
  instructorId: string
): Promise<void> => {
  try {
    const organizationId = getDefaultOrganizationId();
    await adminV1OrganizationsPaymentsServiceInstructorsDestroy(
      organizationId,
      instructorId
    );
  } catch (error: any) {
    console.error(
      `Error deleting instructor with ID ${instructorId}:`,
      error.response?.data || error.message || error
    );
    throw error;
  }
};

export const getBookingSessionAssignmentsByFacility = async (
  params: AdminV1OrganizationsBookingSessionAssignmentsFacilityRetrieveParams
): Promise<any> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const response =
      await adminV1OrganizationsBookingSessionAssignmentsFacilityRetrieve(
        organizationId,
        params
      );
    // The API returns grouped facility data with sessions array
    return response;
  } catch (error: any) {
    console.error(
      "Error fetching booking session assignments by facility:",
      error
    );
    throw error;
  }
};

export const createBooking = async (
  bookingRequest: AdminBookingRequest
): Promise<AdminServiceBooking> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsPaymentsServiceBookingsCreate(
      organizationId,
      bookingRequest
    );

    return result;
  } catch (error: any) {
    console.error(
      `Error creating booking:`,
      error.response?.data || error.message || error
    );
    throw error;
  }
};
